import React, { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { dashboardApi } from '@/api/dashboard.api';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { TicketTable } from '@/components/tickets/TicketTable';
import { TicketFiltersBar } from '@/components/tickets/TicketFiltersBar';
import { useRealtime } from '@/context/RealtimeContext';
import { useFiltersInUrl } from '@/hooks/useFiltersInUrl';
import type { ListTicketsQuery, TicketSummary } from '@/types/api';

/**
 * V4.9 — My Queue redesigned with the same filter bar as All Tickets,
 * plus a Refresh button that gives clear feedback (toast on success).
 *
 * Since the my-queue endpoint returns a pre-filtered list (only tickets
 * assigned to the current user), filtering happens client-side. The
 * data set is small (typically <100 rows), so this is fine.
 */
export function MyQueuePage() {
  const queryClient = useQueryClient();
  // V4.9.5+ — URL-backed filter state. My Queue uses per_page=100 by default
  // (small client-side list, fits comfortably on one page) — pass that to
  // useFiltersInUrl so the default is correctly omitted from the URL.
  const [filters, setFilters] = useFiltersInUrl({
    page: 1,
    per_page: 100,
    sort: 'smart'
  });

  const myQueue = useQuery({
    queryKey: ['dashboard', 'myQueue'],
    queryFn: () => dashboardApi.myQueue()
  });

  // V4.9.5 — Live refresh on any ticket event so My Queue stays current
  // even when other users create / change tickets you're assigned to.
  const { on } = useRealtime();
  useEffect(() => {
    const invalidate = () => {
      void queryClient.invalidateQueries({ queryKey: ['dashboard', 'myQueue'] });
    };
    const off1 = on('ticket.created', invalidate);
    const off2 = on('ticket.status_changed', invalidate);
    const off3 = on('ticket.updated', invalidate);
    const off4 = on('ticket.approval_decided', invalidate);
    const off5 = on('ticket.cx_informed', invalidate);
    return () => {
      off1();
      off2();
      off3();
      off4();
      off5();
    };
  }, [on, queryClient]);

  const filtered = useMemo(() => {
    const tickets = myQueue.data ?? [];
    return applyClientSideFilters(tickets, filters);
  }, [myQueue.data, filters]);

  // V4.9.2 — Refresh = hard window reload (per ops request).
  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div>
      <div className="section-header">
        <h2>
          My queue{' '}
          {myQueue.data ? (
            <span className="text-muted text-small">
              · {filtered.length}
              {filtered.length !== myQueue.data.length ? ` of ${myQueue.data.length}` : ''}
            </span>
          ) : null}
        </h2>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={handleRefresh}
          disabled={myQueue.isFetching}
        >
          {myQueue.isFetching ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>
      {/* V4.9.2 — `card-overflow-visible` so MultiSelect dropdowns aren't clipped. */}
      <div className="card card-overflow-visible">
        <TicketFiltersBar value={filters} onChange={setFilters} />
      </div>
      <div className="card mt-4">
        <div className="card-body no-pad">
          {myQueue.isLoading ? (
            <LoadingState label="Loading your queue..." />
          ) : myQueue.isError ? (
            <ErrorState error={myQueue.error} onRetry={() => myQueue.refetch()} />
          ) : (
            <TicketTable
              tickets={filtered}
              emptyLabel={
                (myQueue.data ?? []).length === 0
                  ? 'No active tickets assigned to you.'
                  : 'No tickets match the current filters.'
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * V4.9 — Client-side filter/sort for the My Queue list. Mirrors the
 * server-side filter semantics in tickets.service so behaviour is
 * consistent across All Tickets and My Queue.
 */
function applyClientSideFilters(tickets: TicketSummary[], q: ListTicketsQuery): TicketSummary[] {
  let result = [...tickets];

  if (q.search) {
    const term = q.search.trim().toLowerCase();
    if (term) {
      result = result.filter((t) => {
        // V4.9.19 — search every field present on a list row: ticket #,
        // summary, customer, order/tracking #, category, and every assigned
        // person. (requested_action is detail-only, not on list rows, so it is
        // searchable in the full All-Tickets server query but not here.)
        const haystack = [
          t.id,
          t.summary,
          t.customer_name,
          t.customer_email,
          t.order_number,
          t.tracking_number,
          t.category,
          t.owner?.name,
          t.owner?.alias,
          t.communicator?.name,
          t.communicator?.alias,
          t.solver?.name,
          t.solver?.alias,
          t.raised_by?.name,
          t.raised_by?.alias
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(term);
      });
    }
  }

  if (q.status && (q.status as string[]).length > 0) {
    const set = new Set(q.status as string[]);
    result = result.filter((t) => set.has(t.status));
  }
  if (q.category && (q.category as string[]).length > 0) {
    const set = new Set(q.category as string[]);
    result = result.filter((t) => set.has(t.category));
  }
  if (q.store && (q.store as string[]).length > 0) {
    const set = new Set(q.store as string[]);
    result = result.filter((t) => set.has(t.store_code));
  }
  if (q.severity && (q.severity as string[]).length > 0) {
    const set = new Set(q.severity as string[]);
    result = result.filter((t) => set.has(t.severity));
  }
  if (q.breach_flag && (q.breach_flag as string[]).length > 0) {
    const set = new Set(q.breach_flag as string[]);
    result = result.filter((t) => set.has(t.breach_flag));
  }

  // V4.9.14 BUGFIX — "Created" date range. My Queue filters client-side, so
  // (unlike All Tickets, which filters server-side) the date range has to be
  // applied HERE too. Without it, picking Today / Yesterday / Last 7 days /
  // Last 30 days / This month / Last month updated the dropdown + URL but the
  // visible list never changed ("nothing happens"). Matches the backend:
  // created_at within [created_after, created_before].
  if (q.created_after) {
    const after = new Date(q.created_after).getTime();
    if (!Number.isNaN(after)) {
      result = result.filter((t) => {
        const c = new Date(t.created_at).getTime();
        return !Number.isNaN(c) && c >= after;
      });
    }
  }
  if (q.created_before) {
    const before = new Date(q.created_before).getTime();
    if (!Number.isNaN(before)) {
      result = result.filter((t) => {
        const c = new Date(t.created_at).getTime();
        return !Number.isNaN(c) && c <= before;
      });
    }
  }

  // Sort
  const sort = q.sort ?? 'smart';
  if (sort === 'smart') {
    result.sort(smartSort);
  } else if (sort === 'awaited_by') {
    // V4.9.3 — Group tickets by the SAME person currently holding them up.
    // Mirrors backend tier logic (Solver/Communicator → Admin → External → Done).
    result.sort(awaitedBySort);
  } else {
    const [field, direction] = sort.split('.');
    const dir = direction === 'desc' ? -1 : 1;
    result.sort((a, b) => {
      const av = pickSortField(a, field);
      const bv = pickSortField(b, field);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }

  return result;
}

/**
 * V4.9.3 — Group by the person currently awaited, with sensible tier order.
 *   Tier 1 — Solver / Communicator (real teammate)  → group by their name A→Z
 *   Tier 2 — Approver (Admin)
 *   Tier 3 — External wait (Customer / Vendor / Design)
 *   Tier 4 — Resolved / Cancelled
 */
function awaitedBySort(a: TicketSummary, b: TicketSummary): number {
  const tier = (t: TicketSummary): number => {
    const r = t.action_required_by?.role;
    if (r === 'done' || r === 'cancelled') return 4;
    if (r === 'customer' || r === 'vendor' || r === 'design') return 3;
    if (r === 'approver') return 2;
    return 1; // solver / communicator / owner
  };
  const nameOf = (t: TicketSummary): string => {
    const n = t.action_required_by?.name ?? '';
    return n.toLowerCase();
  };
  const ta = tier(a);
  const tb = tier(b);
  if (ta !== tb) return ta - tb;
  const na = nameOf(a);
  const nb = nameOf(b);
  if (na !== nb) return na < nb ? -1 : 1;
  // Tiebreak — severity, then SLA, then created
  if (a.severity !== b.severity) return a.severity < b.severity ? -1 : 1;
  const sa = new Date(a.sla_deadline).getTime();
  const sb = new Date(b.sla_deadline).getTime();
  if (sa !== sb) return sa - sb;
  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
}

function pickSortField(t: TicketSummary, field: string): string | number | null {
  if (field === 'created_at') return new Date(t.created_at).getTime();
  if (field === 'sla_deadline') return new Date(t.sla_deadline).getTime();
  if (field === 'severity') return t.severity;
  if (field === 'status') return t.status;
  return null;
}

/**
 * V4.9.5 — Smart sort mirrors the new backend tier logic.
 *   Tier 1   BREACHED  (overlay on any live ticket)
 *   Tier 2   Active work (Investigation in progress, Approved reprint in progress, legacy In-progress)
 *   Tier 3   Open
 *   Tier 4   Awaiting approval
 *   Tier 5   Approved decisions (waiting to act)
 *   Tier 6   Awaiting CX (or legacy external waits)
 *   Tier 99  Terminal (Completed, Cancelled, Reprint/Replacement/Refund completed, legacy Resolved)
 */
const TERMINAL_STATUSES = new Set([
  'Completed',
  'Cancelled',
  'Reprint completed',
  'Replacement completed',
  'Approved refund completed',
  'Resolved' // legacy
]);
const ACTIVE_STATUSES = new Set([
  'Investigation in progress',
  'Approved reprint in progress',
  'In-progress' // legacy
]);
const APPROVAL_AWAITING = new Set([
  'Investigation in progress - awaiting approval',
  'Waiting Approval' // legacy
]);
const APPROVED_DECISIONS = new Set([
  'Approved for replacement',
  'Approved reprint'
]);

function smartSort(a: TicketSummary, b: TicketSummary): number {
  const tier = (t: TicketSummary): number => {
    if (TERMINAL_STATUSES.has(t.status)) return 99;
    if (t.breach_flag === 'BREACHED') return 1;
    if (ACTIVE_STATUSES.has(t.status)) return 2;
    if (t.status === 'Open') return 3;
    if (APPROVAL_AWAITING.has(t.status)) return 4;
    if (APPROVED_DECISIONS.has(t.status)) return 5;
    if (t.status === 'Investigation in progress - awaiting CX feedback') return 6;
    if (t.status.startsWith('Waiting')) return 6;
    return 50;
  };
  const ta = tier(a);
  const tb = tier(b);
  if (ta !== tb) return ta - tb;
  // Severity (P1 < P2 < P3 < P4 alphabetically)
  if (a.severity !== b.severity) return a.severity < b.severity ? -1 : 1;
  // SLA deadline soonest first
  const sa = new Date(a.sla_deadline).getTime();
  const sb = new Date(b.sla_deadline).getTime();
  if (sa !== sb) return sa - sb;
  // Newest first
  const ca = new Date(a.created_at).getTime();
  const cb = new Date(b.created_at).getTime();
  return cb - ca;
}
